import { create } from 'zustand';

import { getSettings } from '../../profile/ProfileStore';
import { isDownloaded } from './assets';
import type { WorkerRequest, WorkerResponse } from './worker/piperWorker';

/**
 * Main-thread client for the Piper worker.
 *
 * Owns what the worker cannot: the settings, request/response pairing, and the decision not to exist. Mirrors
 * `core/prediction/usePrediction.ts`, deliberately — a second worker in this codebase should be recognisable as
 * the same shape as the first.
 *
 * ── STOP, honestly ──
 *
 * `InferenceSession.run()` has no abort. So when the person hits STOP mid-sentence, this cannot stop the wasm
 * mid-inference: it stops **playback**, which is the part that is audible, and discards the chunk the worker is
 * still computing. Non-negotiable §3.3 asks for silence within ~100 ms and that is what it gets; what it does not
 * get is the CPU back for another few hundred milliseconds. The alternative — tearing down the worker on every
 * STOP — would throw away the 60 MB session and make the next sentence cost a second to start.
 */

interface PiperState {
  /** Null until checked. False when the assets are absent, which is not a failure. */
  available: boolean | null;
  ready: boolean;
  failed: boolean;
  sampleRate: number | null;
  /** How long the ONNX session took to build, for the device measurement. */
  sessionMs: number | null;
  start: () => Promise<void>;
  stopWorker: () => void;
}

let worker: Worker | null = null;
let nextId = 1;

/** Resolvers for synth requests in flight, keyed by request id. */
const pending = new Map<
  number,
  {
    resolve: (value: { pcm: Float32Array<ArrayBuffer>; sampleRate: number }) => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }
>();

/**
 * How long a chunk may take before the worker is presumed dead (`SPK-17`, `SPK-20`, C81).
 *
 * There was no timeout, and `ready` is a flag set when the worker started — so a worker iOS killed
 * while the app was backgrounded still reads as ready, the chain keeps choosing it, and every utterance
 * waits for a reply that is never coming.
 *
 * Scaled by length against Phase 0's measured RTF of 0.19 (2036 ms for 35 words), with generous slack:
 * the cost of being wrong here is falling back to a working voice, and the cost of no timeout is silence.
 */
const CHUNK_BUDGET_BASE_MS = 1500;
const CHUNK_BUDGET_PER_WORD_MS = 150;
const CHUNK_BUDGET_CAP_MS = 6000;

export function chunkBudgetMs(text: string): number {
  const words = text.trim() === '' ? 0 : text.trim().split(/\s+/u).length;
  return Math.min(CHUNK_BUDGET_CAP_MS, CHUNK_BUDGET_BASE_MS + words * CHUNK_BUDGET_PER_WORD_MS);
}

/** Outstanding liveness checks, keyed by request id. Separate from `pending`: a pong carries no audio. */
const alive = new Map<number, () => void>();

/** Settle a chunk and stop its deadline. Every exit from `pending` goes through here or `abandonPending`. */
function settlePending(id: number, settle: (entry: NonNullable<ReturnType<typeof pending.get>>) => void): void {
  const entry = pending.get(id);
  if (!entry) return;
  clearTimeout(entry.timer);
  pending.delete(id);
  settle(entry);
}

export const usePiper = create<PiperState>((set) => ({
  available: null,
  ready: false,
  failed: false,
  sampleRate: null,
  sessionMs: null,

  start: async () => {
    if (worker) return;

    // Nothing loads while the feature is off: no worker, no ORT, no 90 MB. `piperEnabled` ships false and the
    // download is a deliberate act in care mode.
    if (!getSettings().piperEnabled) {
      set({ available: false });
      return;
    }

    if (!(await isDownloaded())) {
      // The assets have not been fetched. The provider reports itself unavailable and the chain moves on, which
      // is exactly what 2.8 asks for — "the chain must simply skip this provider".
      set({ available: false });
      return;
    }

    try {
      worker = new Worker(new URL('./worker/piperWorker.ts', import.meta.url), { type: 'module' });
    } catch (err) {
      console.error('[piper] worker failed to start', err);
      set({ failed: true, available: false });
      return;
    }

    worker.onmessage = (event: MessageEvent<WorkerResponse>): void => {
      const message = event.data;

      if (message.type === 'ready') {
        set({ ready: true, available: true, sampleRate: message.sampleRate, sessionMs: message.sessionMs });
        console.info('[piper] session ready', { sessionMs: Math.round(message.sessionMs) });
        return;
      }

      if (message.type === 'pong') {
        alive.get(message.id)?.();
        return;
      }

      if (message.type === 'audio') {
        settlePending(message.id, (entry) =>
          entry.resolve({ pcm: message.pcm, sampleRate: message.sampleRate }),
        );
        return;
      }

      // An error with no id is initialisation failing; the feature is simply not available.
      if (message.id === undefined) {
        console.warn('[piper] unavailable', message.message);
        set({ failed: true, available: false, ready: false });
        for (const entry of pending.values()) {
          clearTimeout(entry.timer);
          entry.reject(new Error(message.message));
        }
        pending.clear();
        return;
      }

      settlePending(message.id, (entry) => entry.reject(new Error(message.message)));
    };

    worker.onerror = (event) => {
      console.error('[piper] worker error', event.message);
      set({ failed: true, available: false, ready: false });
    };

    post({ type: 'init' });
  },

  stopWorker: () => {
    worker?.terminate();
    worker = null;
    // Through abandonPending rather than pending.clear(), so the deadlines die with the requests: a
    // terminated worker's outstanding timers would otherwise fire and flag a worker that no longer exists.
    abandonPending();
    for (const settle of alive.values()) settle();
    alive.clear();
    set({ ready: false, available: null, sampleRate: null, sessionMs: null });
  },
}));

function post(message: WorkerRequest): void {
  worker?.postMessage(message);
}

/**
 * Synthesise one chunk.
 *
 * Rejects rather than resolving empty, so the provider can fall through to the next in the chain instead of
 * playing silence — a person who hears nothing has no way to tell "failed" from "spoken while muted".
 */
export async function synthesiseChunk(
  text: string,
): Promise<{ pcm: Float32Array<ArrayBuffer>; sampleRate: number }> {
  if (!worker || !usePiper.getState().ready) throw new Error('piper: not ready');

  const id = nextId++;
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      pending.delete(id);
      /**
       * **Mark the worker failed, not just this chunk.**
       *
       * This is what turns "the second tap works" into "the first tap works next time": without the
       * flip, `isAvailable()` keeps returning true and every following utterance pays the same wait
       * before falling through. `SPK-20` is the requirement; the repair is what brings it back.
       */
      usePiper.setState({ ready: false, failed: true });
      reject(new Error('piper: chunk timeout'));
    }, chunkBudgetMs(text));

    pending.set(id, { resolve, reject, timer });
    post({ type: 'synth', id, text });
  });
}

/**
 * Discard a request nobody is listening to. The worker finishes it regardless.
 *
 * **Clears the timers too.** Otherwise an abandoned chunk's deadline fires later and marks a perfectly
 * healthy worker dead — which the lookahead makes routine rather than rare: `PiperLocal` always has one
 * chunk in flight that the end of a sentence makes pointless.
 */
export function abandonPending(): void {
  for (const entry of pending.values()) {
    clearTimeout(entry.timer);
    entry.reject(new Error('piper: abandoned'));
  }
  pending.clear();
}

/**
 * Ask the worker whether it is still there (`SPK-20`).
 *
 * A flag set at startup cannot answer this: iOS may have killed the worker while the app was
 * backgrounded and nothing tells the main thread. Used by the repair, which rebuilds the 60 MB session
 * only when this does not answer — that cost is right for a repair a human asked for and wrong anywhere
 * automatic.
 */
export async function checkAlive(timeoutMs = 1500): Promise<boolean> {
  if (!worker) return false;

  const id = nextId++;
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => {
      alive.delete(id);
      resolve(false);
    }, timeoutMs);

    alive.set(id, () => {
      clearTimeout(timer);
      alive.delete(id);
      resolve(true);
    });

    post({ type: 'ping', id });
  });
}
